<template>
  <div>
    <button :class="[$style.button, isLiked && $style.isLiked]" @click="onClick">
      <SvgIcon :svg="isLiked ? HeartFillSvg : HeartActiveSvg" :class="$style.heartIcon" />
      {{ likedCount }}
    </button>
    <div :class="$style.balloon">
      <LikeBalloon :liked="isLiked" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { countLikes, likeEvent, unlikeEvent } from '@/js/lib/api/event';
import { useEventStore } from '@/js/lib/store/event';
import { useUserStore } from '@/js/lib/store/user';
import { loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import SvgIcon from '@/js/components/molecules/SvgIcon.vue';
import LikeBalloon from '@/js/components/www.iichi.com/organisms/EventLikeBalloon.vue';
import HeartActiveSvg from '@/svgs/vue/heart-active.svg';
import HeartFillSvg from '@/svgs/vue/heart-fill.svg';

const props = defineProps<{
  eventId: number;
}>();
const userStore = useUserStore();
const eventStore = useEventStore();
const isLoading = ref(false);
const isLiked = computed(() => eventStore.likedEventIds.includes(props.eventId));
const likedCount = ref(0);

const like = async () => {
  eventStore.addLiked(props.eventId);
  likedCount.value += 1;
  await likeEvent({ eventId: props.eventId }).catch(() => {
    eventStore.removeLiked(props.eventId);
    likedCount.value -= 1;
  });
};
const unlike = async () => {
  eventStore.removeLiked(props.eventId);
  likedCount.value -= 1;
  await unlikeEvent({ eventId: props.eventId }).catch(() => {
    eventStore.addLiked(props.eventId);
    likedCount.value += 1;
  });
};
const onClick = async () => {
  if (userStore.isAuthenticated) {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    await (isLiked.value ? unlike() : like());
    isLoading.value = false;
  } else {
    signinSignup({
      next: window.location.href,
      callback: async () => {
        await loading({
          asyncFunc: async () => {
            like();
            window.location.reload();
          },
          text: 'ログインしています',
        });
      },
    });
  }
};

onMounted(async () => {
  likedCount.value = await countLikes(props.eventId);
  eventStore.checkLiked([props.eventId]);
});
</script>

<style module lang="scss">
.button {
  align-items: center;
  background-color: transparent;
  border-style: none;
  cursor: pointer;
  display: flex;
  font-size: 18px;
  gap: 4px;
  text-decoration: none;

  @media (--sp) {
    font-size: 16px;
  }

  &:hover {
    opacity: 0.8;
  }

  .heartIcon {
    fill: #4b4b4b;
    width: 48px;

    @media (--sp) {
      width: 44px;
    }
  }

  &.isLiked .heartIcon {
    fill: var(--action-button-heart-color);
  }
}

.balloon {
  bottom: 0;
  position: absolute;
  right: 20px;
}
</style>
